import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";

import Button from "components/lib/Shared/Button";
import SubscriptionsTable from "./Table";
import SubscriptionForm from "./Form";
import SubscriptionView from "./View";
import {
  getSubscriptionsAsync,
  updateSubscriptionState,
} from "state/slices/subscription";
import { Constants } from "config/constants";
import SubscriptionsCards from "./Cards";
import ManageSubsription from "./ManageSubsription";
import { ClockIcon, LockClosedIcon } from "@heroicons/react/outline";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";

const SubscriptionPlans = ({ manageSubscriptions = true }) => {
  const cookieRole = Cookies.get("role");
  const dispatch = useDispatch();
  const [showAddSubscriptionForm, setShowAddSubscriptionForm] = useState(false);
  const [billingTimeline, setBillingTimeline] = useState("monthly");
  const { subscriptions, selectedSubscription, mode, getSubscriptions } =
    useSelector((state) => state.subscriptionSlice);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const { organization, organizationName } = useSelector(
    (state) => state.activeDashboardSlice
  );
  const { organization: corporate } = useSelector(
    (state) => state.organizationsSlice
  );

  const { company } = useSelector((state) => state.companyDashboardSlice);

  let orgAdmin = null;
  let showOrgAdmin = false;
  if (company?.orgAdmin) {
    orgAdmin = company?.orgAdmin;
    showOrgAdmin = true;
  }
  if (corporate?.orgAdmin) {
    orgAdmin = corporate?.orgAdmin;
    showOrgAdmin = true;
  }
  if (organization?.isOrgAdmin) {
    orgAdmin = organization._id;
    showOrgAdmin = manageSubscriptions;
  }

  const currentSubscription =
    subscriptions && subscriptions.items?.length > 0
      ? subscriptions.items.find(
          (subsc) =>
            subsc.name ===
            (organization?.activeCustomerSubscription?.subscription?.name ||
              corporate?.activeCustomerSubscription?.subscription?.name ||
              company?.activeCustomerSubscription?.subscription?.name)
        )
      : null;

  useEffect(() => {
    dispatch(
      getSubscriptionsAsync({
        orgAdmin: showOrgAdmin ? orgAdmin : "",
      })
    );
    return () => {
      dispatch(
        updateSubscriptionState({
          selectedSubscription: {
            features: [],
            currencies: [],
          },
        })
      );
    };
  }, []);

  useEffect(() => {
    if (getSubscriptions.status === "loading") {
      setSubscriptionsLoading(true);
    } else {
      setSubscriptionsLoading(false);
    }
  }, [getSubscriptions.status]);

  if (mode === "view") {
    return (
      <SubscriptionView
        setShowAddSubscriptionForm={setShowAddSubscriptionForm}
      />
    );
  }

  if (
    showAddSubscriptionForm ||
    (selectedSubscription?._id && mode === "edit")
  ) {
    return (
      <SubscriptionForm
        setShowAddSubscriptionForm={setShowAddSubscriptionForm}
      />
    );
  }

  if (selectedSubscription?._id && mode === "manage") {
    return <ManageSubsription />;
  }

  const handleAddNewSubscription = () => {
    dispatch(
      updateSubscriptionState({
        selectedSubscription: {
          features: [],
          currencies: [],
        },
      })
    );
    setShowAddSubscriptionForm(true);
  };

  const changeBillingTimeline = (newTimeline) => {
    setBillingTimeline(newTimeline);
  };

  return (
    <div className="pl-4">
      <div className="flex justify-end mb-4">
        {manageSubscriptions && (
          <Button onClick={handleAddNewSubscription} className={"bg-primary"}>
            Add new subscription plan
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-8">
        {!manageSubscriptions &&
          cookieRole !== Constants.COOKIE_ROLES.SUPER_ADMIN && (
            <div className="flex flex-col gap-4">
              <div>
                <h1 className="font-semibold">Current Subscription</h1>
                <p className="text-sm">
                  Key metrics and perfomance indicators for you platform
                </p>
              </div>
              <div>
                <div className="grid grid-cols-3 md:grid-cols-6 rounded-xl rounded-b-none bg-white shadow p-6">
                  <div className="col-span-1 md:col-span-2 flex items-top gap-2">
                    <div>
                      <LockClosedIcon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <p className="text-xs text-gray-600">Organization</p>
                      <h1 className="font-semibold text-primary">
                        {subscriptionsLoading ? (
                          <SpinnerIcon className="text-gray-600" />
                        ) : (
                          organizationName ||
                          corporate?.names?.companyName ||
                          company?.registrationName ||
                          company?.companyName1
                        )}
                      </h1>
                    </div>
                  </div>
                  <div className="col-span-1">
                    <div className="flex flex-col gap-1">
                      <p className="text-xs text-gray-600">Current Plan</p>
                      <h1 className=" font-semibold text-primary">
                        {currentSubscription
                          ? currentSubscription.name
                          : "Not Selected"}
                      </h1>
                    </div>
                  </div>

                  <div className="col-span-1 md:col-span-3 text-end">
                    <div>
                      {subscriptionsLoading ? (
                        <SpinnerIcon className="text-gray-600" />
                      ) : currentSubscription ? (
                        <p>
                          <span className="font-bold text-2xl text-primary">
                            {currentSubscription?.currencies[0]?.type}{" "}
                            {currentSubscription?.currencies[0]?.basePrice}
                          </span>
                          <span className="text-gray-600"> /month</span>
                        </p>
                      ) : (
                        <p>
                          <span className="font-bold text-2xl text-primary">
                            KSH 0
                          </span>
                          <span className="text-gray-600"> /month</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-6 rounded-xl rounded-t-none bg-amber-50 shadow p-6">
                  <div className="col-span-1 md:col-span-2 flex items-top gap-2">
                    <div>
                      <ClockIcon className="w-5 h-5" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <p className="text-sm font-semibold">
                        Next billing date:{" "}
                      </p>
                      <h1 className="text-xs">
                        Days remaining in the current cycle
                      </h1>
                    </div>
                  </div>
                  <div className="col-span-1 ">
                    <div className="flex flex-col gap-1">
                      <p className="text-sm font-semibold">Auto Renew </p>
                      <h1 className="text-xs"></h1>
                    </div>
                  </div>
                  <div className="col-span-1 md:col-span-3 text-end">
                    <div>
                      {subscriptionsLoading ? (
                        <SpinnerIcon className="text-gray-600" />
                      ) : (
                        <Button
                          disabled
                          className="bg-white border border-gray-300 text-primary text-sm"
                        >
                          View Payment Details
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        <div className="flex flex-col gap-4">
          {!manageSubscriptions && (
            <div className="flex justify-between">
              <h1 className="font-semibold">Choose Your Plan</h1>
              <div className="flex gap-4 text-sm">
                <Button
                  onClick={() => changeBillingTimeline("monthly")}
                  className={`p-1 ${
                    billingTimeline === "monthly"
                      ? "bg-sflPrimary "
                      : "bg-white text-primary"
                  }`}
                >
                  Monthly
                </Button>
                <Button
                  onClick={() => changeBillingTimeline("annual")}
                  className={`p-1 ${
                    billingTimeline === "annual"
                      ? "bg-sflPrimary"
                      : "bg-white text-primary"
                  }`}
                >
                  Annual
                </Button>
              </div>
            </div>
          )}
          {manageSubscriptions && (
            <div>
              <h1 className="font-semibold text-primary">Client Fees</h1>
              <p className="text-sm">
                Manage global subscriptions settings and view detailed reports
              </p>
            </div>
          )}
          {/* <SubscriptionsTable /> */}
          <SubscriptionsCards manageSubscriptions={manageSubscriptions} />
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
